import { Component, Input, OnInit, ViewChild, ElementRef, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { noop, Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  BaseButtonSize,
  BaseInquiryStatus,
  baseInquiryActions,
  BaseCoreState,
  baseFormsActions,
  BASE_PERMISSIONS,
  BaseRolesService,
  BaseModalService,
  BaseInquiryStatusValue,
  BaseInquiryType,
} from 'base';
import { BaseCustomerInquiryService } from '../../../inquiry.service';
import { BaseCustomerServiceProviderSelectService } from '../../../../../shared/_services/base-customer-service-provider-select.service';
import { BaseCustomerSharedService } from '../../../../../shared/_services/base-customer-shared.service';
import { BaseCustomerInquiryListItemType } from '../../../_interfaces/base-inquiry.type';

@Component({
  selector: 'base-customer-inquiry-list-item',
  templateUrl: './base-customer-inquiry-list-item.component.html',
})
export class BaseCustomerInquiryListItemComponent implements OnChanges, OnInit, OnDestroy {
  @ViewChild('staffRequest') staffRequest: ElementRef;
  @ViewChild('editStaffRequest') editStaffRequest: ElementRef;
  @ViewChild('closeInquiryModal') closeInquiryModal: ElementRef;
  @ViewChild('serviceProviderSelectModal', { static: true }) serviceProviderSelectModal: ElementRef;
  @ViewChild('inquiredServiceProvidersModal') inquiredServiceProvidersModal: ElementRef;

  @Input() inquiry: BaseCustomerInquiryListItemType | undefined;
  @Input() canPublishInquiry = false;
  @Input() copiedInquiry: BaseInquiryType | undefined;
  @Input() filters: { [key in any]: any };

  baseInquiryStatus = BaseInquiryStatus;
  baseInquiryStatusValue = BaseInquiryStatusValue;
  buttonSizes = BaseButtonSize;
  canPreselectOffers = false;
  newStaffRequestModalRef: NgbModalRef;
  editStaffRequestModalRef: NgbModalRef;
  onInquiryCloseModalRef: NgbModalRef;
  inquiredServiceProvidersModalRef: NgbModalRef;
  serviceProviderSelectModalRef: NgbModalRef;
  unpublishedInquiryId: number;
  inquiryActions: string[] = [];
  hasCloseInquiryPermission: boolean;
  editingInquiry: BaseInquiryType | undefined;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private store: Store<BaseCoreState>,
    private baseCustomerInquiryService: BaseCustomerInquiryService,
    private rolesService: BaseRolesService,
    private modalService: BaseModalService,
    private serviceProviderService: BaseCustomerServiceProviderSelectService,
    private sharedService: BaseCustomerSharedService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.inquiry && !changes.firstChange) {
      this.inquiryActions = this.baseCustomerInquiryService.setInquiryActions(
        this.inquiry as BaseCustomerInquiryListItemType,
        this.canPublishInquiry,
        this.hasCloseInquiryPermission
      );
    }
  }

  ngOnInit() {
    this.canPreselectOffers = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_PRESELECT_OFFERS);
    this.hasCloseInquiryPermission = this.rolesService.hasCustomerFeaturePermission(
      BASE_PERMISSIONS.FEATURE_CLOSE_INQUIRY
    );
    this.inquiryActions = this.baseCustomerInquiryService.setInquiryActions(
      this.inquiry as BaseCustomerInquiryListItemType,
      this.canPublishInquiry,
      this.hasCloseInquiryPermission
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleInquiryClick() {
    if (!this.inquiry) return;

    const { id, unlimited, status } = this.inquiry;

    if (status === BaseInquiryStatus.unpublished) {
      return unlimited ? this.openEditInquiryModal(id) : this.onEdit(id);
    }

    const route = unlimited ? `inquiries/${id}/unlimited/offers` : `inquiries/${id}/offers`;
    void this.router.navigate([route]);
  }

  copyInquiryHandler(event: Event) {
    if (!this.inquiry) {
      return;
    }

    event.stopPropagation();
    this.copyInquiry(this.inquiry.id as number);
  }

  copyInquiry(inquiryId: number) {
    this.store.dispatch(baseFormsActions.clearAccountRegisterForm());
    this.baseCustomerInquiryService
      .getInquiry(inquiryId)
      .pipe(
        takeUntil(this.destroy$),
        map((inquiry) => inquiry as BaseInquiryType)
      )
      .subscribe((inquiry) => {
        this.copiedInquiry = { ...inquiry, id: -1 };

        const isMixedShifts = this.baseCustomerInquiryService.defineIsShiftTypesMixed(this.copiedInquiry);

        if (isMixedShifts) {
          this.baseCustomerInquiryService.openShiftViewPage(this.copiedInquiry, this.inquiry?.id as number, true);
        } else {
          this.openNewStaffRequestModal();
        }
      });
  }

  handleClick(actionType: string, id: number) {
    switch (actionType) {
      case 'action.edit':
        this.handleEditAction(id);
        break;
      case 'action.close':
        this.onClose(id);
        break;
      case 'action.publish':
        this.onPublish(id);
        break;
    }
  }

  openNewStaffRequestModal() {
    this.newStaffRequestModalRef = this.modalService.open(
      {
        headText: 'inquiry.new',
        bodyTemplate: this.staffRequest,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );
    this.newStaffRequestModalRef.result.then(noop, noop);
  }

  openEditInquiryModal(inquiryId: number) {
    this.baseCustomerInquiryService
      .getInquiry(inquiryId)
      .pipe(
        takeUntil(this.destroy$),
        map((inquiry) => inquiry as BaseInquiryType)
      )
      .subscribe((inquiry) => {
        this.editingInquiry = inquiry;
        this.editStaffRequestModalRef = this.modalService.open(
          {
            headText: 'inquiry.editInquiry',
            bodyTemplate: this.editStaffRequest,
            hideFooter: true,
          },
          {
            backdrop: 'static',
            size: 'lg',
          }
        );
        this.editStaffRequestModalRef.result.then(noop, noop);
      });
  }

  openInquiredServiceProvidersModal(event: Event) {
    if (!this.inquiry) {
      return;
    }

    event.stopPropagation();
    this.store.dispatch(
      baseInquiryActions.loadInquiryProviders({
        payload: { inquiryId: this.inquiry.id as number },
      })
    );
    this.inquiredServiceProvidersModalRef = this.modalService.open(
      {
        headText: 'inquiry.inquiredServiceProvidersModal.headText',
        bodyTemplate: this.inquiredServiceProvidersModal,
        okButtonDisplayed: false,
        cancelButtonOnly: true,
        cancelText: 'action.close',
      },
      {
        backdrop: 'static',
        size: 'xl',
      }
    );
    this.inquiredServiceProvidersModalRef.result.then(noop, noop);
  }

  private onEdit(inquiryId: number) {
    this.baseCustomerInquiryService.getInquiry(inquiryId).subscribe((inquiry) => {
      this.baseCustomerInquiryService.openShiftViewPage(inquiry, inquiryId);
    });
  }

  private onClose(inquiryId: number): void {
    this.onInquiryCloseModalRef = this.modalService.open(
      {
        headText: 'inquiry.closeInquiry.headText',
        bodyTemplate: this.closeInquiryModal,
        hideFooter: true,
      },
      {
        scrollable: false,
        backdrop: true,
        size: 'lg',
      }
    );

    this.onInquiryCloseModalRef.result.then((value: string) => {
      this.updateCustomerInquiryStatus(inquiryId, BaseInquiryStatus.closed, value);
    }, noop);
  }

  private onPublish(inquiryId: number): void {
    this.unpublishedInquiryId = inquiryId;

    if (!!this.inquiry?.directlyInquired) {
      this.sharedService.publishInquiryDirectly(inquiryId, this.inquiry.directlyInquired.profile.pspBranches, false);
    } else {
      this.serviceProviderSelectModalRef = this.serviceProviderService.openInquiryVisibilityAndNotificationsModal(
        this.serviceProviderSelectModal
      );

      this.serviceProviderSelectModalRef.result.then(() => {
        this.updateCustomerInquiryStatus(inquiryId, BaseInquiryStatus.published);
      }, noop);
    }
  }

  private handleEditAction(id: number): void {
    if (this.inquiry?.unlimited) {
      this.openEditInquiryModal(id);
    } else {
      this.onEdit(id);
    }
  }

  private updateCustomerInquiryStatus(inquiry: number, status: 1 | 2 | 3, closedNote?: string): void {
    this.store.dispatch(
      baseInquiryActions.updateInquiryStatus({
        payload: {
          inquiry,
          status,
          closedNote,
          callback: () => {
            this.store.dispatch(
              baseInquiryActions.getInquiries({
                payload: this.activatedRoute.snapshot.queryParams,
              })
            );
          },
        },
      })
    );
  }
}
